/**
 * Custom hook to handle user information fetching and authentication flow
 * Manages user data fetching, login method validation, and error handling
 */

// React and Next.js core imports
import { useCallback } from "react"
import { useRouter, usePathname } from "next/navigation"

// External libraries and utilities
import { type Address } from "viem"
import { type User } from "@privy-io/react-auth"
import { openModal } from "@locmod/modal"
import { sendGTMEvent } from "@next/third-parties/google"
import localStorage from "@locmod/local-storage"

// Internal application modules
import { ConnectorName, useWallet } from "wallet"
import { getSignedRulesStatus, getUserAccountApi } from "src/utils/api"
import { type UserAccountData } from "src/utils/helper"
import { getAuthMethod } from "src/utils"
import { useUserContext } from "src/contexts/user"
import { useNudge } from "src/contexts/nudge"
import messages from "@/compositions/modals/ErrorModal/messages"
import { constants } from "helpers"

interface UtmParams {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

interface UseUserInfoFetchProps {
  isAAWallet: boolean
  user: User | null
  loginMethod: string | null
  connector: { name: ConnectorName } | null
  storedInviteCode: string | null
  storedUtmParams: UtmParams
}

export const useUserInfoFetch = ({
  isAAWallet,
  user,
  loginMethod,
  connector,
  storedInviteCode,
  storedUtmParams,
}: UseUserInfoFetchProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const { loginUser, logoutUser, setLoginCompleted, setUserConnecting } =
    useUserContext()
  const { logoutApp } = useWallet()
  const { openNudge, closeNudge } = useNudge()

  /**
   * Fetches and processes user information
   * Handles:
   * - Wallet address validation
   * - User data fetching
   * - Login method verification
   * - Error handling and user feedback
   * - Analytics tracking
   */
  const getUserInfo = useCallback(async () => {
    // Get wallet address based on wallet type
    const walletAddress = isAAWallet
      ? user?.smartWallet?.address
      : user?.wallet?.address

    if (!walletAddress) {
      console.warn("Wallet address is missing.")
      return
    }

    setUserConnecting()

    // Prepare error message with wallet address
    const text = {
      ...messages.loginMethodNotAllowed,
      value: { walletAddress },
    }

    try {
      // Check if user has signed rules
      const status = await getSignedRulesStatus(walletAddress as Address)
      // Send referral code for users who haven't signed consent yet (is_consent_signed = false)
      // This allows legitimate referral code updates while preventing abuse
      const shouldSendReferralCode = !status.signed && storedInviteCode

      // Prepare request object with UTM params and invite code (for users without consent)
      const reqObj = Object.fromEntries(
        Object.entries({
          ...(shouldSendReferralCode && { invite_code: storedInviteCode }),
          ...storedUtmParams,
          login_method: !isAAWallet ? "wallet" : "social",
        }).filter(([_, value]) => value)
      )

      // Fetch user account data
      const response: UserAccountData = await getUserAccountApi(reqObj)
      if (response) {
        // Validate login method
        const loginMethodCheck =
          response.login_method === "wallet" &&
          loginMethod &&
          loginMethod !== "siwe"

        if (loginMethodCheck) {
          logoutApp()
          openModal("ErrorModal", {
            title: messages.loginMethodNotAllowedTitle,
            text,
          })
          throw new Error(text.en)
        }

        // Process successful login
        loginUser(response)

        // Handle unsigned rules
        if (!status.signed) {
          openModal("SignMessageModal")
        }

        // Show nudge for unverified users
        if (!response.is_verified) {
          openNudge({
            message:
              "Link email and join 10,000+ members who've already unlocked premium benefits - limited spots remaining",
          })
        }

        // Close nudge if user is already verified
        if (response.is_verified) {
          closeNudge()
        }

        // Track successful sign-in
        if (status.signed) {
          setLoginCompleted(true)
          // Set isNewUserFlag based only on response.new_account
          const isNewUserFlag = Boolean(response.new_account)
          // Determine auto-login state
          let isAutoLogin = false
          if (typeof window !== "undefined") {
            isAutoLogin =
              localStorage.getItem("manualLoginThisSession") !== "true" &&
              localStorage.getItem("hasLoggedOut") !== "true"
            // Clear both flags after login
            localStorage.removeItem("manualLoginThisSession")
            localStorage.removeItem("hasLoggedOut")
          }
          // Always provide wallet_app_name
          let walletAppName =
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name
          if (!walletAppName) {
            if (isAAWallet) {
              walletAppName = "privy"
            } else {
              walletAppName = "null"
            }
          }
          sendGTMEvent({
            event: "sign_in_completed",
            user_id: response.id,
            login_toolkit: "privy",
            login_method:
              (isAAWallet && loginMethod === "siwe" ? "siwe" : loginMethod) ||
              "wallet",
            login_method_id:
              (isAAWallet && getAuthMethod(user as User, loginMethod)?.value) ||
              walletAddress,
            is_auto_login: isAutoLogin,
            is_new_user: isNewUserFlag,
            first_name: response.first_name,
            last_name: response.last_name,
            email_address: response.email_id,
            wallet_app_name: walletAppName,
            wallet_address: response.wallet_address,
            referral_code: storedInviteCode,
          })
          // Fire wallet_sign_approved event after successful sign-in
          sendGTMEvent({
            event: "wallet_sign_approved",
            user_id: response.id,
            wallet_address: response.wallet_address,
            is_new_user: isNewUserFlag,
            is_auto_login: isAutoLogin,
            email_address: response.email_id,
            wallet_app_name: walletAppName,
          })
        }

        // Mark user info as fetched
        localStorage.setItem(
          constants.localStorageKeys.hasFetchedUserInfo,
          "true"
        )
        router.replace(pathname!)

        return response
      }
    } catch (error) {
      console.error("getUserInfo error:", error)
      // Clear fetch status on error
      localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
      // Always reset loading state on error
      setLoginCompleted(false)
      throw error
    }
    // Removed finally block with setTimeout to prevent infinite loops
  }, [
    isAAWallet,
    user,
    loginMethod,
    connector,
    storedInviteCode,
    storedUtmParams,
    setUserConnecting,
    setLoginCompleted,
    loginUser,
    logoutUser,
    logoutApp,
    openNudge,
    closeNudge,
    router,
    pathname,
  ])

  return { getUserInfo }
}
